<template>
  <Transition name="bug-reward-toast">
    <output v-if="count > 0" class="bug-reward-toast">
      <p class="copy">{{ copy }}</p>
    </output>
  </Transition>
</template>

<script setup lang="ts">
import { computed, onUnmounted, watch } from 'vue'
import { useI18n } from '../i18n/t'
import {
  ackBugRewardNotice,
  bugRewardNoticeCount,
} from '../account/bugRewardNotice'

const TOAST_MS = 8_000

const { t } = useI18n()
const count = bugRewardNoticeCount
const copy = computed(() =>
  count.value === 1
    ? t('bugReward.toast')
    : t('bugReward.toastMany', { n: count.value }),
)
let hideTimer: ReturnType<typeof setTimeout> | null = null

function clearTimer(): void {
  if (hideTimer) {
    clearTimeout(hideTimer)
    hideTimer = null
  }
}

watch(
  count,
  (n) => {
    clearTimer()
    if (n <= 0) return
    void ackBugRewardNotice()
    hideTimer = setTimeout(() => {
      bugRewardNoticeCount.value = 0
    }, TOAST_MS)
  },
  { immediate: true },
)

onUnmounted(() => {
  clearTimer()
})
</script>

<style scoped>
.bug-reward-toast {
  position: fixed;
  left: 50%;
  bottom: calc(156px + env(safe-area-inset-bottom, 0px));
  z-index: 10050;
  display: flex;
  align-items: center;
  width: min(520px, calc(100vw - 32px));
  margin: 0;
  padding: 12px 16px;
  border-radius: 12px;
  background: var(--md-sys-color-surface-container-high);
  color: var(--md-sys-color-on-surface);
  box-shadow: var(--md-elevation-2);
  transform: translateX(-50%);
  font-family: Roboto, system-ui, sans-serif;
}

.copy {
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
}

.bug-reward-toast-enter-active,
.bug-reward-toast-leave-active {
  transition:
    opacity var(--md-motion-short) var(--md-motion-standard),
    transform var(--md-motion-short) var(--md-motion-standard);
}

.bug-reward-toast-enter-from,
.bug-reward-toast-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(8px);
}

@media (prefers-reduced-motion: reduce) {
  .bug-reward-toast-enter-active,
  .bug-reward-toast-leave-active {
    transition: none;
  }
}
</style>
